import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UserStateStoreService } from '@optima-stride-ai/optima-platform';
import { IUser } from '@optima-stride-ai/core';
import {
  HAYAGRIVA_DATA_PACKS,
  HayagrivaDataPack,
  HayagrivaDataGenService,
  HayagrivaGenConfig,
} from './hayagriva-data-gen.service';

/** User-friendly labels for each file type key */
const FILE_TYPE_LABELS: Record<string, string> = {
  presentation: 'Presentations (.pptx)',
  markdown: 'Markdown (.md)',
  doc: 'Documents (.docx)',
  spreadsheet: 'Spreadsheets (.xlsx)',
  drawing: 'Drawings (.odg)',
};

@Component({
  selector: 'app-hayagriva-data-gen-panel',
  templateUrl: './hayagriva-data-gen-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
})
export class HayagrivaDataGenPanelComponent implements OnInit {
  private readonly dataGenService = inject(HayagrivaDataGenService);
  private readonly userStateStoreService = inject(UserStateStoreService);

  // ── Admin user selection ────────────────────────────────────────────────
  readonly availableUsers = signal<IUser[]>([]);
  readonly selectedAdminUserNames = signal<Set<string>>(new Set());
  readonly isLoadingUsers = signal(false);

  // ── Reset Structure state ──────────────────────────────────────────────────
  readonly confirmSeed = signal(false);
  readonly isSeedingOnly = signal(false);
  readonly seedResult = signal<{ success: boolean; error?: string } | null>(null);

  // ── Generate Files state ───────────────────────────────────────────────────
  readonly packs: HayagrivaDataPack[] = HAYAGRIVA_DATA_PACKS;
  readonly selectedPackId = signal(HAYAGRIVA_DATA_PACKS[0].id);
  readonly selectedPack = computed(
    () => this.packs.find((p) => p.id === this.selectedPackId()) ?? this.packs[0],
  );
  readonly showConfigForm = computed(() => this.selectedPack().configurable);

  readonly config = signal<HayagrivaGenConfig>(
    structuredClone(HAYAGRIVA_DATA_PACKS[0].defaultConfig),
  );
  readonly isRunning = signal(false);
  readonly result = signal<{
    success: boolean;
    filesCreated: number;
    filesFailed: number;
    error?: string;
  } | null>(null);

  // ── Shared ─────────────────────────────────────────────────────────────────
  readonly logs = this.dataGenService.logs;
  readonly fileTypeEntries = computed(() => Object.entries(this.config().fileTypeCounts));
  readonly fileTypeLabels = FILE_TYPE_LABELS;

  // ── Reset Structure actions ──────────────────────────────────────────────
  ngOnInit(): void {
    this.loadUsers();
  }

  private async loadUsers(): Promise<void> {
    this.isLoadingUsers.set(true);
    try {
      const users = await this.userStateStoreService.getAll();
      this.availableUsers.set(users);
      this.selectedAdminUserNames.set(new Set(users.map(u => u.name ?? '').filter(Boolean)));
    } catch {
      this.availableUsers.set([]);
    } finally {
      this.isLoadingUsers.set(false);
    }
  }

  isAdminUserSelected(userName: string): boolean {
    return this.selectedAdminUserNames().has(userName);
  }

  toggleAdminUser(userName: string): void {
    this.selectedAdminUserNames.update(set => {
      const next = new Set(set);
      next.has(userName) ? next.delete(userName) : next.add(userName);
      return next;
    });
  }

  selectAllAdminUsers(): void {
    this.selectedAdminUserNames.set(new Set(this.availableUsers().map(u => u.name ?? '').filter(Boolean)));
  }

  deselectAllAdminUsers(): void {
    this.selectedAdminUserNames.set(new Set());
  }

  private getSkipList(): string[] {
    return this.availableUsers()
      .map(u => u.name ?? '')
      .filter(name => name && !this.selectedAdminUserNames().has(name));
  }
  async seedOnly(): Promise<void> {
    if (this.isSeedingOnly() || !this.confirmSeed()) return;
    this.isSeedingOnly.set(true);
    this.seedResult.set(null);
    try {
      const outcome = await this.dataGenService.seedOnly(this.getSkipList());
      this.seedResult.set(outcome);
      this.confirmSeed.set(false);
    } finally {
      this.isSeedingOnly.set(false);
    }
  }

  // ── Generate Files actions ─────────────────────────────────────────────────
  selectPack(packId: string): void {
    const pack = this.packs.find((p) => p.id === packId);
    if (!pack) return;
    this.selectedPackId.set(packId);
    this.config.set(structuredClone(pack.defaultConfig));
    this.result.set(null);
  }

  updateFileTypeCount(key: string, value: number): void {
    const cfg = this.config();
    this.config.set({
      ...cfg,
      fileTypeCounts: { ...cfg.fileTypeCounts, [key]: Math.max(0, value) },
    });
  }

  async generate(): Promise<void> {
    if (this.isRunning()) return;
    this.isRunning.set(true);
    this.result.set(null);

    try {
      const outcome = await this.dataGenService.generateAll(this.config(), this.getSkipList());
      this.result.set(outcome);
    } finally {
      this.isRunning.set(false);
    }
  }
}
